import { useState, useEffect } from 'react';
import { supabase, auth } from '@/lib/supabase-client';
import type { Session, User, AuthChangeEvent } from '@supabase/supabase-js';

// Extend the Supabase User to have Appwrite-compatible properties 
// to prevent breaking existing components during migration
export type MappedUser = User & {
    $id: string;
    name: string;
    emailVerification: boolean;
    $createdAt: string;
    $updatedAt: string;
    prefs: Record<string, any>;
};

export function useAuth() {
    const [current, setCurrent] = useState<Session | null>(null);
    const [rawUser, setRawUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [authKey, setAuthKey] = useState(0);

    const register = async (email: string, password: string): Promise<void> => {
        try {
            const { data, error } = await auth.signUp(email, password);
            if (error) throw error;
            // Sometimes sign up requires email confirmation. If session is returned, we're logged in.
            if (data?.session) {
                setCurrent(data.session);
                setRawUser(data.user);
                setAuthKey(prev => prev + 1);
            }
        } catch (error) {
            console.error('Registration error:', error);
            throw error;
        }
    };

    const login = async (email: string, password: string): Promise<void> => {
        try {
            const { data, error } = await auth.signIn(email, password);
            if (error) throw error;

            setCurrent(data.session);
            setRawUser(data.user);
            setAuthKey(prev => prev + 1);
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    };

    const logout = async (): Promise<void> => {
        try {
            await auth.signOut();
            setCurrent(null);
            setRawUser(null);
            setAuthKey(prev => prev + 1);
        } catch (error) {
            console.error('Logout error:', error);
            throw error;
        }
    };

    const getCurrentUser = async () => {
        try {
            setLoading(true);
            const { data: { session }, error } = await supabase.auth.getSession();
            if (error) throw error;

            setCurrent(session);
            setRawUser(session?.user ?? null);
        } catch (error) {
            console.error('Auth check error:', error);
            setCurrent(null);
            setRawUser(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getCurrentUser();

        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event: AuthChangeEvent, session: Session | null) => {
            setCurrent(session);
            setRawUser(session?.user ?? null);
            setAuthKey(prev => prev + 1);
            // Don't set loading=false here - getCurrentUser controls initial load to prevent redirect flash
        });

        return () => {
            subscription.unsubscribe();
        };
    }, []);

    const user: MappedUser | null = rawUser ? {
        ...rawUser,
        $id: rawUser.id,
        name: rawUser.user_metadata?.name || rawUser.user_metadata?.full_name || rawUser.email?.split('@')[0] || 'User',
        emailVerification: !!rawUser.email_confirmed_at,
        $createdAt: rawUser.created_at,
        $updatedAt: rawUser.updated_at || rawUser.created_at,
        prefs: rawUser.user_metadata?.prefs || {},
    } as MappedUser : null;

    return {
        current,
        user,
        loading,
        authKey,
        login,
        logout,
        register,
        refetch: getCurrentUser,
    };
}